import React from "react";
//eslint-disable-next-line no-restricted-imports
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useHistory } from "react-router-dom";
import { colors, css, styled } from "../../stitches.config";
import { baseActionLink } from "../Inputs/__styles__/DropdownMenu";
import { ActionsProps, getTextColor } from "../Inputs/DropdownMenu";
import { ActionCellContainer } from "./__styles__/ActionCell";
import { Button } from "./Button";
import { ActionButton } from "./Buttons/ActionButton";
import { Body } from "./Typography";

export const ActionCell = (props: ActionsMenuProps) => {
  return (
    <ActionCellContainer>
      <Actions {...props} />
    </ActionCellContainer>
  );
};

export const ACTION_COLUMN_DEF_CONSTANTS = {
  id: "actions",
  minSize: 50,
  sortable: false,
};

const Action = ({ action }: { action: ActionsProps }) => {
  const history = useHistory();
  const className = actionLink({
    variant: action.disabled ? "disabled" : action.variant,
  });
  const { href, track, onClick, target, label, download } = action;

  const onSelect = (event: Event) => {
    if (track) {
      track();
    }

    if (action.to) {
      // we prevent default here because otherwise there's a memory
      // leak - after the history is pushed and the URL has been updated,
      // the dropdown component tries to close itself *but*
      // the page has already re-rendered to a different page, meaning
      // the dropdown itself has unmounted. event.preventDefault() keeps
      // the dropdown from closing and avoids the memory leak, which
      // is causing noise in prod and in tests.
      event.preventDefault();
      history.push(action.to);
    }

    if (onClick) {
      onClick();
    }
  };

  const isLink = !!href;

  const textColor = getTextColor(action);

  return (
    <DropdownItem
      onSelect={onSelect}
      className={className}
      tabIndex={0}
      asChild={isLink}
      disabled={action.disabled}
      aria-disabled={action.disabled}
    >
      {isLink ?
        <a
          href={href}
          download={download}
          target={target}
          data-testid={`link-for-${label}`}
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          <Body size="large" type="regular" color={textColor}>
            {label}
          </Body>
        </a>
      : <Body
          size="large"
          type="regular"
          color={textColor}
          style={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {label}
        </Body>
      }
    </DropdownItem>
  );
};

type ActionsMenuProps = {
  actions: Array<ActionsProps>;
  buttonText?: string;
  disabled?: boolean;
  customButton?: React.FC<{
    onClick: (e: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
  }>;
  onMenuDidOpen?: () => void;
};

export const Actions = ({
  actions,
  buttonText,
  disabled,
  customButton,
  onMenuDidOpen,
}: ActionsMenuProps) => {
  const [open, setOpen] = React.useState(false);

  const toggleOpen = () => {
    setOpen(open => !open);
    if (!open) {
      onMenuDidOpen?.();
    }
  };

  const actionButton =
    customButton ? customButton({ onClick: toggleOpen, disabled })
    : buttonText ?
      <Button
        styleVariant="secondary"
        size="small"
        tabIndex={0}
        disabled={disabled}
        data-testid="actions"
        aria-label="actions"
        onClick={toggleOpen}
      >
        {buttonText}
      </Button>
    : <ActionButton
        onClick={toggleOpen}
        isDisabled={disabled}
        data-testid="actions"
        aria-label="actions"
      />;

  return (
    <div>
      {actionButton}
      <Dropdown.Root modal={false} open={open} onOpenChange={setOpen}>
        <Dropdown.Trigger asChild>
          <div />
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <DropdownContent
            sideOffset={4}
            collisionPadding={16}
            align="end"
            data-testid={"actions-list"}
          >
            {actions
              .filter(action => !action.hidden)
              .map((action, i) => {
                return <Action action={action} key={i} />;
              })}
          </DropdownContent>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
};

//these styles have to be in the same file as the radix dropdown to work:
const DropdownContent = styled(Dropdown.Content, {
  background: colors.bgUiSurface,
  border: 1,
  borderColor: colors.borderUi,
  borderStyle: "solid",
  borderRadius: 4,
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  display: "flex",
  flexDirection: "column",
  padding: "8px 0",
  zIndex: 10,
  maxWidth: "275px",
});

const DropdownItem = styled(Dropdown.Item, {
  display: "flex",
  alignItems: "center",
  gap: 4,

  "&:hover": {
    backgroundColor: colors.bgUiContainer,
  },
  "&:focus-visible": {
    outline: "none",
    backgroundColor: colors.bgUiInteraction,
  },

  cursor: "pointer",

  variants: {
    hide: {
      true: {
        display: "none",
      },
    },
  },
});

const actionLink = css({
  ...baseActionLink,
  width: "100%",
  height: "100%",
});
